import { SatPopoverModule } from '@ncstate/sat-popover';
import { NgxChartsModule } from '@swimlane/ngx-charts';
import { ResizableModule } from 'angular-resizable-element';
import { NgxLoadingButtonsModule } from 'ngx-loading-buttons';
import { NgxMaterialTimepickerModule } from 'ngx-material-timepicker';
import { ToastContainerModule } from 'ngx-toastr';

import { DragDropModule } from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatNativeDateModule, MatRippleModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialogModule } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatSliderModule } from '@angular/material/slider';
import { MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';

import { AxonModule } from '@lib/axon';
import {
  AuthDigestWarningComponent,
  CameraSearchComponent,
  CameraViewPopupComponent,
  ConfirmLivestreamDevicesDialogComponent,
} from '@lib/cameras/components';
import { SnCommonModule } from '@lib/common';
import { SnFormModule } from '@lib/form';
import { SnFormAutocompleteModule } from '@lib/form/autocomplete';
import { SnFormCheckboxModule } from '@lib/form/checkbox';
import { FormDateTimepickerModule } from '@lib/form/date-time-picker';
import { SnDragSlideToggleModule } from '@lib/form/drag-slide-toggle';
import { SnRadialSliderModule } from '@lib/form/radial-slider';
import { GoogleMapModule } from '@lib/geo/google/map';
import { SnMapModule } from '@lib/geo/map';
import { IntegrationsVerkadaModule } from '@lib/integrations-verkada';
import { LvtModule } from '@lib/lvt';
import { SnImagePlayerModule } from '@lib/player/image-player';
import { SnVideoPlayerModule } from '@lib/player/video-player';
import { SnSchedulerModule } from '@lib/scheduler';
import { SnShareModule } from '@lib/share';
import { SnStatsModule } from '@lib/stats';
import { SnTagsModule } from '@lib/tags';
import { TimelineModule } from '@lib/timeline';
import { TranslationModule } from '@lib/translation';
import { SnActionsModule } from '@lib/ui/actions';
import { SnAlertModule } from '@lib/ui/alert';
import { SnButtonModule } from '@lib/ui/button';
import { SnDataListModule } from '@lib/ui/data-list';
import { SnDrawerModule } from '@lib/ui/drawer';
import { SnIconModule } from '@lib/ui/icon';
import { SnListModule } from '@lib/ui/list';
import { SnSearchbarModule } from '@lib/ui/searchbar';
import { SnTooltipModule } from '@lib/ui/tooltip';
import { CameraEventsListComponent } from 'libs/cameras/src/lib/components/camera-events-list/camera-events-list.component';

import {
  SnCameraGridComponent,
  SnCameraListComponent,
  SnCameraFormComponent,
  CameraStatusComponent,
  SnCameraDetailsComponent,
  CameraGridTableComponent,
  CameraTableComponent,
  SnCameraListItemComponent,
  SnCameraStreamViewComponent,
  SnCameraRecordStatusComponent,
  SnCameraTableDetailsComponent,
  CameraAiGridDialogComponent,
  SnCameraScheduleDialogComponent,
  AddCameraGroupDialogComponent,
  AddCameraToGroupDialogComponent,
  CameraSearchDialogComponent,
  OperationalGridViewComponent,
  CameraFormDrawerComponent,
  CamFeedComponent,
  CamFeedCardComponent,
  CamPlaybackCardComponent,
  FususCameraDetectionsDialogComponent,
  FususCameraFormDialogComponent,
  CamerasByFloorDialogComponent,
  PlaceCamerasDialogComponent,
} from './components';
import { CameraAddRuleDialogComponent } from './components/camera-add-rule-dialog/camera-add-rule-dialog.component';
import { CameraGroupTableComponent } from './components/camera-group-table/camera-group-table.component';
import { CameraReportDialogComponent } from './components/camera-report-dialog/camera-report-dialog.component';
import { CameraRulesDialogComponent } from './components/camera-rules-dialog/camera-rules-dialog.component';
import { CameraViewTabsComponent } from './components/camera-view-tabs/camera-view-tabs.component';
import { CameraWsOverlayComponent } from './components/camera-ws-overlay/camera-ws-overlay.component';
import { DetectionImageDialogComponent } from './components/detection-image-dialog/detection-image-dialog.component';
import {
  SnAuditCameraGridContainer,
  SnCameraDetailsDrawerContainer,
  SnAuditCameraStreamViewContainer,
} from './containers';
import { FususCameraEventComponent } from './containers/fusus-camera-event/fusus-camera-event.container';

@NgModule({
  imports: [
    // Angular
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    // CDK
    DragDropModule,
    NgxChartsModule,
    // Material
    MatButtonModule,
    MatButtonModule,
    MatCardModule,
    MatCheckboxModule,
    MatChipsModule,
    MatDatepickerModule,
    MatDialogModule,
    MatExpansionModule,
    MatGridListModule,
    MatListModule,
    MatMenuModule,
    MatNativeDateModule,
    MatProgressBarModule,
    MatProgressSpinnerModule,
    MatRippleModule,
    MatSelectModule,
    MatSliderModule,
    MatSortModule,
    MatTableModule,
    MatTabsModule,
    MatToolbarModule,
    MatTooltipModule,
    NgxMaterialTimepickerModule,
    SnCommonModule,
    ToastContainerModule,
    // Libs
    AxonModule,
    FormDateTimepickerModule,
    GoogleMapModule,
    IntegrationsVerkadaModule,
    LvtModule,
    NgxLoadingButtonsModule,
    ResizableModule,
    SatPopoverModule,
    SnActionsModule,
    SnAlertModule,
    SnButtonModule,
    SnDataListModule,
    SnDragSlideToggleModule,
    SnDrawerModule,
    SnFormCheckboxModule,
    SnFormModule,
    SnIconModule,
    SnImagePlayerModule,
    SnListModule,
    SnMapModule,
    SnRadialSliderModule,
    SnSchedulerModule,
    SnSearchbarModule,
    SnShareModule,
    SnStatsModule,
    SnTagsModule,
    SnTooltipModule,
    SnVideoPlayerModule,
    TimelineModule,
    SnFormAutocompleteModule,
    TranslationModule,
  ],
  exports: [
    // Components
    CameraTableComponent,
    SnCameraGridComponent,
    SnCameraListComponent,
    SnCameraFormComponent,
    CameraStatusComponent,
    SnCameraDetailsComponent,
    CameraGridTableComponent,
    SnCameraListItemComponent,
    SnCameraStreamViewComponent,
    SnCameraRecordStatusComponent,
    SnCameraTableDetailsComponent,
    CameraAiGridDialogComponent,
    SnCameraScheduleDialogComponent,
    CameraSearchDialogComponent,
    OperationalGridViewComponent,
    FususCameraEventComponent,
    CameraEventsListComponent,
    CameraFormDrawerComponent,
    CameraSearchComponent,
    CameraViewTabsComponent,
    // Containers
    SnAuditCameraGridContainer,
    SnCameraDetailsDrawerContainer,
    SnAuditCameraStreamViewContainer,
    AddCameraGroupDialogComponent,
    CameraGroupTableComponent,
    AddCameraToGroupDialogComponent,
    CameraWsOverlayComponent,
    DetectionImageDialogComponent,
    CameraSearchComponent,
    CameraRulesDialogComponent,
    CameraAddRuleDialogComponent,
    CameraSearchDialogComponent,
    FususCameraEventComponent,
    CameraViewPopupComponent,
    CamFeedComponent,
    CamFeedCardComponent,
    CamPlaybackCardComponent,
    FususCameraDetectionsDialogComponent,
    FususCameraFormDialogComponent,
    ConfirmLivestreamDevicesDialogComponent,
  ],
  declarations: [
    // Components
    CameraTableComponent,
    SnCameraGridComponent,
    SnCameraListComponent,
    SnCameraFormComponent,
    CameraStatusComponent,
    SnCameraDetailsComponent,
    CameraGridTableComponent,
    SnCameraListItemComponent,
    SnCameraStreamViewComponent,
    SnCameraRecordStatusComponent,
    SnCameraTableDetailsComponent,
    CameraAiGridDialogComponent,
    SnCameraScheduleDialogComponent,
    CameraSearchDialogComponent,
    OperationalGridViewComponent,
    FususCameraEventComponent,
    CameraEventsListComponent,
    CameraFormDrawerComponent,
    CamFeedComponent,
    CamFeedCardComponent,
    CamPlaybackCardComponent,
    CamerasByFloorDialogComponent,
    PlaceCamerasDialogComponent,
    // Containers
    SnAuditCameraGridContainer,
    SnCameraDetailsDrawerContainer,
    SnAuditCameraStreamViewContainer,
    AddCameraGroupDialogComponent,
    CameraGroupTableComponent,
    AddCameraToGroupDialogComponent,
    CameraWsOverlayComponent,
    DetectionImageDialogComponent,
    CameraSearchComponent,
    CameraRulesDialogComponent,
    CameraAddRuleDialogComponent,
    CameraViewPopupComponent,
    CameraReportDialogComponent,
    FususCameraDetectionsDialogComponent,
    FususCameraFormDialogComponent,
    CameraViewTabsComponent,
    ConfirmLivestreamDevicesDialogComponent,
    AuthDigestWarningComponent,
  ],
})
export class SnCamerasModule {}
